import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import deviceGroupService from "../services/deviceGroupService";
import softwareUpdateService, {
  defaultDeadlineLocal,
  softwareUpdateVersionLabel,
  toTargetLocalDateTime,
} from "../services/softwareUpdateService";
import SoftwareUpdateCatalogPicker from "../components/SoftwareUpdateCatalogPicker";

export default function SoftwareUpdates() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const canEdit = isAdministrator();
  const [groups, setGroups] = useState([]);
  const [iosCatalog, setIosCatalog] = useState([]);
  const [macCatalog, setMacCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [suIdentifier, setSuIdentifier] = useState(null);
  const [suDeadline, setSuDeadline] = useState(defaultDeadlineLocal());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const loadCatalogs = async ({ refresh = false } = {}) => {
    setLoadingCatalog(true);
    setCatalogError("");
    try {
      const catalogs = await softwareUpdateService.getCatalogs({ refresh });
      setIosCatalog(catalogs.ios || []);
      setMacCatalog(catalogs.macos || []);
    } catch (err) {
      setIosCatalog([]);
      setMacCatalog([]);
      setCatalogError(err.response?.data?.message || err.message || "Apple GDMF-Katalog konnte nicht geladen werden.");
    } finally {
      setLoadingCatalog(false);
    }
  };

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const data = await deviceGroupService.getDeviceGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Gruppen konnten nicht geladen werden.");
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    loadCatalogs();
    loadGroups();
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((group) => String(group._id) === String(selectedGroupId)) || null,
    [groups, selectedGroupId]
  );

  useEffect(() => {
    setSuIdentifier(selectedGroup?.softwareUpdateIdentifier || null);
    setSuDeadline(defaultDeadlineLocal());
  }, [selectedGroup]);

  const saveGroup = async () => {
    if (!selectedGroup) {
      toast.error("Bitte eine Gerätegruppe wählen.");
      return;
    }
    setSaving(true);
    try {
      if (suIdentifier) {
        const item = [...iosCatalog, ...macCatalog].find((row) => row.identifier === suIdentifier);
        await softwareUpdateService.setGroupSoftwareUpdate(selectedGroup._id, {
          identifier: suIdentifier,
          family: item?.family,
          productVersion: item?.productVersion,
          targetLocalDateTime: toTargetLocalDateTime(suDeadline),
        });
      } else {
        await softwareUpdateService.setGroupSoftwareUpdate(selectedGroup._id, { identifier: "none" });
      }
      toast.success("Gruppenvorgabe gespeichert. Geräte ohne Override werden aktualisiert.");
      await loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container">
      <h2 className="mb-3">Software Updates</h2>
      <p className="text-muted mb-4">
        Zielversionen kommen aus Apple GDMF. Die Gerätegruppe setzt die Vorgabe für alle Mitglieder.
        Einzelne Geräte können sie unter dem Tab Software Updates überschreiben oder zurücksetzen.
      </p>

      <div className="card mb-4 p-3">
        <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center mb-3">
          <h5 className="mb-0">Gruppenvorgabe</h5>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={loadingCatalog}
            onClick={() => loadCatalogs({ refresh: true })}
          >
            Katalog aktualisieren
          </button>
        </div>
        {loadingGroups ? (
          <p className="text-muted">Lade Gruppen…</p>
        ) : groups.length === 0 ? (
          <p className="mb-0">
            Noch keine Gruppen. Bitte unter <Link to="/device-groups">Geräte-Gruppen</Link> anlegen.
          </p>
        ) : (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Gerätegruppe</label>
                <select
                  className="form-select"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                >
                  <option value="">Gruppe wählen…</option>
                  {groups.map((group) => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                      {group.softwareUpdateIdentifier
                        ? ` · ${softwareUpdateVersionLabel(group.softwareUpdateIdentifier)}`
                        : " · keine Vorgabe"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Deadline (lokal)</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={suDeadline}
                  onChange={(e) => setSuDeadline(e.target.value)}
                  disabled={!canEdit}
                />
              </div>
            </div>
            {selectedGroup ? (
              <>
                <p className="small text-muted mb-1">
                  Aktuell: {selectedGroup.softwareUpdateIdentifier
                    ? softwareUpdateVersionLabel(selectedGroup.softwareUpdateIdentifier)
                    : "keine Vorgabe"}
                  {selectedGroup.memberCount != null ? ` · ${selectedGroup.memberCount} Gerät(e)` : ""}
                </p>
                <p className="small text-muted">
                  Die Gruppenliste zeigt den gesamten GDMF-Katalog. Am Gerät gelten nur Versionen ab der installierten OS-Version und für das jeweilige Modell. Ein Downgrade ist mit DDM nicht möglich.
                </p>
                <SoftwareUpdateCatalogPicker
                  iosCatalog={iosCatalog}
                  macCatalog={macCatalog}
                  value={suIdentifier}
                  onChange={setSuIdentifier}
                  loading={loadingCatalog}
                  error={catalogError}
                  onRetry={() => loadCatalogs({ refresh: true })}
                  name="page-su"
                />
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-primary mt-2"
                    disabled={saving || loadingCatalog}
                    onClick={saveGroup}
                  >
                    {saving ? "Speichere…" : "Vorgabe speichern"}
                  </button>
                )}
              </>
            ) : (
              <p className="small text-muted mb-0">Gruppe wählen, um eine Zielversion aus dem Katalog zu setzen.</p>
            )}
          </>
        )}
      </div>

      <div className="card p-3">
        <h5 className="mb-3">Aktuelle Gruppenvorgaben</h5>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Gruppe</th>
                <th>OS-Update</th>
                <th>Geräte</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group._id}>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={() => setSelectedGroupId(String(group._id))}
                    >
                      {group.name}
                    </button>
                  </td>
                  <td>
                    {group.softwareUpdateIdentifier
                      ? softwareUpdateVersionLabel(group.softwareUpdateIdentifier)
                      : "—"}
                  </td>
                  <td>{group.memberCount ?? "—"}</td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted">Keine Gruppen.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
